import type { LazyQueryExecFunction } from '@apollo/client';
import type { BusinessAddressRef } from '@seek/hirer-business-address';
import type { PaymentMethodType } from '@seek/payments-widget';

import { saveTrackingData } from 'src/app/shared/dataAnalytics/storage';
import {
  BusinessAddressValidationError,
  DraftClosedError,
} from 'src/app/v2/constants/errors';
import type { DraftJobQuery, Exact, Scalars } from 'src/graphql/generated';
import type { CheckoutComponentCallbacks } from 'src/types/checkout';

export const onPostAdButtonClicked = async ({
  businessAddressRef,
  getDraft,
  paymentMedium,
  paymentMethod,
  isSavedCreditCard,
}: {
  businessAddressRef?: React.RefObject<BusinessAddressRef>;
  getDraft: LazyQueryExecFunction<
    DraftJobQuery,
    Exact<{
      id: Scalars['ID']['input'];
    }>
  >;
  paymentMethod?: PaymentMethodType;
  paymentMedium?: string;
  isSavedCreditCard?: boolean;
}): Promise<void> => {
  saveTrackingData({
    isPaidByStoredCreditCard:
      paymentMethod === 'CreditCard' && Boolean(isSavedCreditCard),
    paymentMediumSelected: paymentMedium || null,
  });

  if (!businessAddressRef) return;

  const validationResult =
    await businessAddressRef.current?.validateAllFields();

  if (validationResult) {
    throw new BusinessAddressValidationError(
      'Business address validation failed',
    );
  }

  await businessAddressRef.current?.submitFormAsync();
  const { data: { draftJob } = {} } = await getDraft();

  if (
    draftJob?.__typename === 'DraftJobNotFoundPayload' &&
    draftJob.reason === 'Not found'
  ) {
    throw new DraftClosedError('Draft is no longer open');
  }
};

export const onReplaceSavedCard: CheckoutComponentCallbacks['onReplaceSavedCard'] =
  () => saveTrackingData({ paymentMethod: 'CreditCard' });

export const onSaveCardOptionChanged: CheckoutComponentCallbacks['onSaveCardOptionChanged'] =
  (shouldSave) => saveTrackingData({ isSaveCreditCardChecked: shouldSave });

export const onSavedCardLoaded: CheckoutComponentCallbacks['onSavedCardLoaded'] =
  () => saveTrackingData({ paymentMethod: 'SavedCreditCard' });

export const onPaymentOptionChanged =
  (
    onChanged: (selectedItem: PaymentMethodType) => void,
  ): CheckoutComponentCallbacks['handlePaymentOptionChange'] =>
  (_selectedIndex, selectedItem) => {
    if (!selectedItem) {
      return;
    }

    const paymentMethod = selectedItem as PaymentMethodType;
    onChanged(paymentMethod);
    saveTrackingData({ paymentMethod });
  };
